import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { agentById, linesByAgent } from "@/data";
import { agentScores } from "@/data/scores";
import { dataset } from "@/data";
import { RadarChart } from "@/components/charts";
import { CellValue, ScoreBar, StatusDot } from "@/components/ui";
import type { Agent } from "@/types";

const SERIES_COLORS = ["#2f6bff", "#e0651f", "#1f9d55", "#a24bd6", "#d64545", "#0f9bb0"];

export default function ComparePage() {
  const [params] = useSearchParams();
  const ids = (params.get("ids") ?? "").split(",").filter(Boolean);
  const agents = ids.map((id) => agentById.get(id)).filter((a): a is Agent => Boolean(a));

  const catDefs = dataset.scoring.categories;

  const radar = useMemo(
    () => ({
      axes: catDefs.map((c) => c.label),
      series: agents.map((a, i) => {
        const s = agentScores.get(a.id)!;
        return {
          label: a.name,
          color: SERIES_COLORS[i % SERIES_COLORS.length],
          values: catDefs.map((c) => s.categories.find((x) => x.id === c.id)?.score ?? null),
        };
      }),
    }),
    [ids.join(",")]
  );

  if (agents.length < 2) {
    return (
      <div className="empty card card-pad">
        Pick at least two agents to compare. Go to <Link to="/">Agents</Link> and tick the boxes,
        then use the compare tray.
      </div>
    );
  }

  const featureRows = dataset.features;

  const Row = ({ label, render }: { label: string; render: (a: Agent) => React.ReactNode }) => (
    <tr>
      <td style={{ color: "var(--text-dim)", fontWeight: 600, position: "sticky", left: 0, background: "var(--surface)" }}>{label}</td>
      {agents.map((a) => (
        <td key={a.id}>{render(a)}</td>
      ))}
    </tr>
  );

  return (
    <div>
      <h1>Compare agents</h1>
      <div className="grid compare-cols">
        <div className="card card-pad">
          <h3>Category profile</h3>
          <RadarChart axes={radar.axes} series={radar.series} />
          <div className="mt">
            {agents.map((a, i) => (
              <div key={a.id} className="row small" style={{ gap: 8 }}>
                <span style={{ width: 12, height: 12, borderRadius: 3, background: SERIES_COLORS[i % SERIES_COLORS.length] }} />
                {a.name}
              </div>
            ))}
          </div>
        </div>

        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th style={{ position: "sticky", left: 0 }}>Attribute</th>
                {agents.map((a) => (
                  <th key={a.id}>
                    <Link to={`/agent/${a.id}`} className="row" style={{ gap: 6, color: "var(--text)" }}>
                      <StatusDot status={a.status.v} /> {a.name}
                    </Link>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <Row label="Overall" render={(a) => <ScoreBar score={agentScores.get(a.id)!.overall} width={70} />} />
              {catDefs.map((c) => (
                <Row
                  key={c.id}
                  label={c.label}
                  render={(a) => {
                    const s = agentScores.get(a.id)!.categories.find((x) => x.id === c.id);
                    return s?.score === null || s === undefined ? <span className="faint">—</span> : s.score.toFixed(0);
                  }}
                />
              ))}
              <Row label="Status" render={(a) => <CellValue cell={a.status} />} />
              <Row label="Founded" render={(a) => <CellValue cell={a.foundingYear} />} />
              <Row label="Website" render={(a) => <CellValue cell={a.website} render={(v) => <a href={v} target="_blank" rel="noreferrer noopener">{v.replace(/^https?:\/\//, "")}</a>} />} />
              <Row label="Service fee" render={(a) => <CellValue cell={a.serviceFee} render={(v: any) => (v.kind === "none" ? "No fee" : v.kind === "percent" ? `${(v.percent * 100).toFixed(1)}%` : v.kind)} />} />
              <Row label="Trustpilot" render={(a) => <CellValue cell={a.trustpilot} render={(v: any) => `${v.score}/5 (${v.reviews})`} />} />
              <Row label="Shipping lines" render={(a) => (linesByAgent.get(a.id)?.length ?? 0) || <span className="faint">—</span>} />
              <Row label="Payments" render={(a) => <CellValue cell={a.paymentMethods} render={(v) => (v as string[]).join(", ")} />} />
              <Row label="Support" render={(a) => <CellValue cell={a.supportChannels} render={(v) => (v as string[]).join(", ")} />} />
              {featureRows.map((f) => (
                <Row
                  key={f.id}
                  label={f.label}
                  render={(a) => {
                    const c = a.features[f.id];
                    if (!c || c.v === null) return <span className="faint">?</span>;
                    return c.v ? <span style={{ color: "var(--good)" }}>✓</span> : <span style={{ color: "var(--bad)" }}>✗</span>;
                  }}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <p className="tiny faint mt">
        “?” = unknown for that agent (not recorded, shown honestly rather than assumed false). Share
        this exact comparison by copying the page URL.
      </p>
    </div>
  );
}
